/**
 * ScrollEffects.js
 *
 * Lightweight scroll-based effects that don't require GSAP:
 * - Progress indicator
 * - Scroll-to-top button visibility
 * - Parallax via CSS custom property (GPU-friendly)
 *
 * For heavy timeline-based animations use Animations.js (GSAP).
 */

import { throttle } from '../utilities/Utilities.js';

export default class ScrollEffects {
  constructor() {
    this._bindScrollProgress();
    this._bindScrollToTop();
    this._bindParallax();
  }

  // ─── Scroll progress bar ────────────────────────────────────────────────────

  _bindScrollProgress() {
    const bar = document.getElementById('scroll-progress');
    if (!bar) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const progress = scrollTop / (scrollHeight - clientHeight);
      bar.style.transform = `scaleX(${progress})`;
    };

    window.addEventListener('scroll', throttle(update, 50), { passive: true });
  }

  // ─── Scroll-to-top button ───────────────────────────────────────────────────

  _bindScrollToTop() {
    const btn = document.getElementById('scroll-top');
    if (!btn) return;

    const toggle = () => {
      const visible = window.scrollY > 400;
      btn.classList.toggle('opacity-0', !visible);
      btn.classList.toggle('pointer-events-none', !visible);
      btn.classList.toggle('translate-y-4', !visible);
    };

    window.addEventListener('scroll', throttle(toggle, 100), { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ─── CSS custom-property parallax ──────────────────────────────────────────
  // Avoids layout thrashing: only writes to a CSS custom property.
  // Usage: <div data-parallax data-speed="0.3"> … </div>
  // CSS:   transform: translateY(calc(var(--parallax-y) * 1px));

  _bindParallax() {
    const elements = document.querySelectorAll('[data-parallax]');
    if (!elements.length) return;

    const update = () => {
      const scrollY = window.scrollY;
      elements.forEach((el) => {
        const speed = parseFloat(el.dataset.speed ?? '0.2');
        el.style.setProperty('--parallax-y', (scrollY * speed).toFixed(2));
      });
    };

    window.addEventListener('scroll', throttle(update, 16), { passive: true });
  }
}
